/**
 * De datumregel onderaan een mededeling (replica-spec §7).
 *
 * De mockup toont drie vormen — "do 18/09 — vr 19/09", "Deze week", "Deadline 30/09" — en dat zijn
 * geen drie velden maar drie uitkomsten van hetzelfde: een periode met een begin, een einde, of
 * allebei. Deze functie rekent de regel uit in plaats van hem te laten typen. Zou de redactie hem
 * typen, dan staat er in maart nog "deze week".
 *
 * Pure functie met de dag van vandaag als argument, zodat ze te testen is zonder de klok te
 * verzetten — dezelfde aanpak als `plannedMomentFor` bij de checklists (ADR-0064).
 */

export interface BulletinPeriod {
  /** `YYYY-MM-DD` of `null`. */
  showFrom: string | null;
  showUntil: string | null;
}

export type BulletinPeriodIcon = 'calendar' | 'clock';

export interface BulletinPeriodLine {
  /** De tekst zoals ze op de kaart staat, of `null` als er geen periode is. */
  text: string | null;
  /** Een kalender bij een periode, een klok bij een termijn — zoals de mockup. */
  icon: BulletinPeriodIcon;
}

const DAGEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

/** `2026-09-18` → `do 18/09`. */
function kort(datum: string): string {
  const [jaar, maand, dag] = datum.split('-').map(Number) as [number, number, number];
  // `Date.UTC` en niet de lokale constructor: anders schuift een datum zonder tijd in een negatieve
  // tijdzone een dag terug, en staat er "wo" boven een mededeling van donderdag.
  const weekdag = DAGEN[new Date(Date.UTC(jaar, maand - 1, dag)).getUTCDay()] as string;
  return `${weekdag} ${String(dag).padStart(2, '0')}/${String(maand).padStart(2, '0')}`;
}

/** Hoeveel hele dagen er tussen twee datums zitten; negatief als de tweede eerder is. */
function dagenTussen(vanaf: string, tot: string): number {
  const dag = 24 * 60 * 60 * 1000;
  return Math.round((Date.parse(`${tot}T00:00:00Z`) - Date.parse(`${vanaf}T00:00:00Z`)) / dag);
}

/**
 * De regel voor één mededeling.
 *
 * @param vandaag De dag van vandaag als `YYYY-MM-DD`, in de tijdzone van het bedrijf.
 */
export function bulletinPeriodLine(period: BulletinPeriod, vandaag: string): BulletinPeriodLine {
  const { showFrom, showUntil } = period;

  // Geen periode: dan hoort er ook geen regel te staan. Een kaart met "—" eronder is ruis.
  if (!showFrom && !showUntil) return { text: null, icon: 'calendar' };

  // Alleen een einde: dat is een termijn, geen periode. De mockup zet er een klok bij.
  if (!showFrom && showUntil) {
    const dagen = dagenTussen(vandaag, showUntil);
    if (dagen === 0) return { text: 'Deadline vandaag', icon: 'clock' };
    if (dagen === 1) return { text: 'Deadline morgen', icon: 'clock' };
    return { text: `Deadline ${kort(showUntil).split(' ')[1] as string}`, icon: 'clock' };
  }

  // Alleen een begin: vanaf wanneer ze geldt.
  if (showFrom && !showUntil) return { text: `Vanaf ${kort(showFrom)}`, icon: 'calendar' };

  const from = showFrom as string;
  const until = showUntil as string;

  // Eén dag: dan is een bereik met twee keer dezelfde datum onzin.
  if (from === until) return { text: kort(from), icon: 'calendar' };

  // Loopt de periode binnen deze week en begint ze vandaag of eerder, dan is "deze week" wat een
  // medewerker eraan heeft — korter en begrijpelijker dan twee datums.
  if (dagenTussen(vandaag, from) <= 0 && dagenTussen(vandaag, until) <= 6) {
    return { text: 'Deze week', icon: 'clock' };
  }

  return { text: `${kort(from)} — ${kort(until)}`, icon: 'calendar' };
}
